import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { AppHeader } from "@/components/AppHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Sparkles, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import type { SnapshotStats } from "@/lib/snapshot-stats";
import { format } from "date-fns";

export const Route = createFileRoute("/snapshots/")({
  head: () => ({
    meta: [
      { title: "Snapshots — Career reports | RoleBoard" },
      {
        name: "description",
        content:
          "Save and revisit your career snapshots — AI insights and key stats from your audition journey.",
      },
      { property: "og:title", content: "Snapshots — RoleBoard" },
      {
        property: "og:description",
        content: "Premium career reports built from your audition history.",
      },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/snapshots" }],
  }),
  component: SnapshotsIndex,
});

type SnapshotRow = {
  id: string;
  created_at: string;
  insight_id: string | null;
  stats_json: SnapshotStats;
  content: { casting_narrative?: string } & Record<string, unknown>;
};

function SnapshotsIndex() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState<SnapshotRow | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [user, authLoading, navigate]);

  async function load() {
    if (!user) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("snapshots")
      .select("id, created_at, insight_id, stats_json, content")
      .order("created_at", { ascending: false });
    if (error) toast.error("Couldn't load snapshots.");
    else setRows((data ?? []) as SnapshotRow[]);
    setLoading(false);
  }

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function handleDelete() {
    if (!toDelete) return;
    const id = toDelete.id;
    setToDelete(null);
    const { error } = await (supabase as any).from("snapshots").delete().eq("id", id);
    if (error) {
      toast.error("Couldn't delete snapshot.");
      return;
    }
    setRows((r) => r.filter((x) => x.id !== id));
    toast("Snapshot deleted.");
  }

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="container max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            Snapshots
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Saved career reports from your insights.
          </p>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <Card className="p-10 text-center">
            <Sparkles className="size-6 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold tracking-tight">No snapshots yet</h3>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
              Generate insights on the Auditions page and save them as a
              snapshot to look back on your progress.
            </p>
            <div className="mt-4">
              <Button asChild>
                <Link to="/auditions" search={{} as never}>
                  <Sparkles className="size-4 mr-1.5" />
                  Go to Insights
                </Link>
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {rows.map((s) => {
              const preview =
                s.content?.casting_narrative ??
                "Your career snapshot.";
              const stats = s.stats_json ?? ({} as SnapshotStats);
              const chips = [
                { label: "Auditions", value: stats.total_auditions ?? 0 },
                {
                  label: "Callback rate",
                  value:
                    stats.callback_rate != null ? `${stats.callback_rate}%` : "—",
                },
                {
                  label: "Top role",
                  value: stats.most_common_role_type ?? "—",
                },
              ];
              return (
                <Link
                  key={s.id}
                  to="/snapshots/$id"
                  params={{ id: s.id }}
                  className="block group focus:outline-none"
                >
                  <Card className="p-5 relative transition-all hover:shadow-md hover:border-primary/40 cursor-pointer h-full">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        Career Snapshot
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(s.created_at), "MMM d, yyyy")}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setToDelete(s);
                          }}
                          className="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          aria-label="Delete snapshot"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm leading-relaxed line-clamp-2">
                      {preview}
                    </p>
                    <div className="flex gap-2 flex-wrap mt-4">
                      {chips.map((c) => (
                        <span
                          key={c.label}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/40 px-2 py-1 text-[11px]"
                        >
                          <span className="text-muted-foreground">{c.label}:</span>
                          <span className="font-medium">{String(c.value)}</span>
                        </span>
                      ))}
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this snapshot?</AlertDialogTitle>
            <AlertDialogDescription>
              This snapshot will be permanently removed. Your auditions and
              insights are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
